import {
  Box,
  Button,
  Grid,
  IconButton,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import { EditRounded } from "@material-ui/icons";
import AddIcon from "@material-ui/icons/Add";
import HighlightOffOutlined from "@material-ui/icons/HighlightOffOutlined";
import { postImages } from "adapters";
import { FileReader } from "components/shared/file-reader";
import { FreeTrialDialog } from "components/shared/free-trial-dialog";
import { theme } from "material-theme";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import Slider, { Settings } from "react-slick";
import { SetUrlBuilderData, UploadType } from "store/url-builder";
import { needsSpecialEmbed } from "utils";
import { DialogWrapper, DialogWrapperType } from "../dialog";
import { FileImportDialog } from "../file-import-dialog";
import style from "./file-carousel.module.scss";

const Carousel: any = withStyles({
  "@global": {
    ".slick-slider": {
      height: "100%",
    },
    ".slick-list": {
      height: "100%",
    },
    ".slick-track": {
      height: "100%",
    },
    ".slick-slide > div": {
      height: "100%",
    },
  },
})(Slider);

export interface FileCarouselProps {
  files: UploadType[];
  themeColor?: string;
  fileLimit?: number;
  readonly?: boolean;
  slidesPerRow?: number;
  enablePreview?: boolean;
  defaultSlideIndex?: number;
  builderType: string;
  isFree?: boolean | null;
  duplicateId?: string;
}

export const FileCarousel = ({
  files = [],
  themeColor,
  fileLimit = 10,
  readonly = false,
  slidesPerRow = 3,
  enablePreview = false,
  defaultSlideIndex = 0,
  builderType = "",
  isFree = null,
  duplicateId,
}: FileCarouselProps) => {
  const useStyles = useCallback(
    makeStyles(() => ({
      dots: {
        bottom: 0,
        "& li.slick-active button::before": {
          color: themeColor + "!important",
        },
      },
    })),
    [themeColor],
  );
  const [slides, setSlides] = useState(files);
  const [importDialog, setImportDialog] = useState<{
    slide?: UploadType;
    index?: number;
    open: boolean;
  } | null>(null);
  const [previewSlide, setPreviewSlide] = useState<{
    slide: UploadType;
    index: number;
  } | null>();
  const [freeTrialDialog, setFreeTrialDialog] = useState(false);
  const dispatch = useDispatch();
  const { cardId }: any = useParams();
  const sliderRef: any = useRef();
  const [replaceInProgres, setReplaceInProgreess] = useState(false);

  const openFreeTrialDialog = () => setFreeTrialDialog(true);
  const handleCloseFreeTrialDialog = () => setFreeTrialDialog(false);

  const classes = useStyles();
  const settings: Settings = {
    dots: true,
    infinite: false,
    slidesPerRow,
    dotsClass: `slick-dots ${classes.dots}`,
    centerPadding: "150px",
    initialSlide: 3,
    draggable: false,
    swipe: false,
  };

  useEffect(() => {
    setSlides(files);
  }, [files]);

  useEffect(() => {
    sliderRef?.current?.slickGoTo(defaultSlideIndex);
  }, [defaultSlideIndex]);

  const handleSlideRemove = async (index: number) => {
    const removeSlide = window.confirm(
      "Are you sure you want to remove selected slide?",
    );
    if (!removeSlide) return;

    let newSlides = slides;
    if (!replaceInProgres) {
      newSlides.splice(index, 1);
      const payload = duplicateId
        ? { userFiles: newSlides, _duplicateId: duplicateId }
        : { userFiles: newSlides, _duplicateId: null };
      const builderResponse = (await postImages(cardId, builderType, payload))
        .data;
      if (builderResponse.success)
        dispatch(SetUrlBuilderData(builderResponse.data));
    } else {
      const emptyUpload: UploadType = {
        _id: "",
        fileName: "",
        filePath: "",
        mimeType: "Empty placeholder",
        updatedAt: "",
      };
      newSlides[index] = emptyUpload;
      setImportDialog({ open: true, index: index });
    }
  };

  const handleFileUploaded = async (file: UploadType) => {
    let newSlides = slides;
    // edit existing slide
    if (importDialog?.index !== undefined) {
      newSlides[importDialog?.index] = file;
    }
    // add new slide
    else newSlides = [...newSlides, file];

    const payload = duplicateId
      ? { userFiles: newSlides, _duplicateId: duplicateId }
      : { userFiles: newSlides, _duplicateId: null };
    const builderResponse = (await postImages(cardId, builderType, payload))
      .data;
    setReplaceInProgreess(false);
    if (builderResponse.success) {
      dispatch(SetUrlBuilderData(builderResponse.data));
      setImportDialog(null);
    }
  };

  const openFilePreview = (slide: UploadType, index: number) => {
    // If the slide has a redirectLink, open it in a new tab instead of showing preview
    if ((slide as any).redirectLink) {
      window.open((slide as any).redirectLink, "_blank");
      return;
    }

    if (enablePreview) return setPreviewSlide({ slide, index });

    if (!/image|video/.test(slide.mimeType)) window.open(slide.fileUrl);
  };

  return (
    <>
      <Carousel
        {...settings}
        ref={sliderRef}
        className={`${style.carousel} file-slide`}
        onInit={console.log}
      >
        {/* When there are no images and the gallery is read-only (no permissions),
            still render a placeholder grid tile so the Gallery section layout is visible. */}
        {readonly && slides.length === 0 && (
          <Box
            key={"readonly-placeholder"}
            boxSizing="border-box"
            padding=".3em"
          >
            <Box
              display="flex"
              // border={"2px dashed #d5d5d5"}
              width="100%"
              height="50px"
              position="relative"
              className="slide-box"
            />
          </Box>
        )}
        {slides.map((slide, idx) => {
          const previewUrl = slide?.fileUrl || "";
          const isSocialEmbed = previewUrl
            ? needsSpecialEmbed(previewUrl)
            : false;
          const showEmbedPlaceholder =
            isSocialEmbed && !readonly && !enablePreview;
          return (
            <Box
              key={idx}
              boxSizing="border-box"
              padding=".3em"
              className="preview-content"
            >
              <Box
                display="flex"
                border={!readonly && "2px dashed #d5d5d5"}
                width="100%"
                height={!enablePreview ? "100%" : "180px"}
                position="relative"
                className={`slide-box ${isSocialEmbed ? style.embedSlideBox : ""}`}
              >
                {!readonly && (
                  <Box
                    className={style.slideActionBar}
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      zIndex: 3,
                      width: "auto",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (isFree) openFreeTrialDialog();
                        else {
                          setReplaceInProgreess(true);
                          setImportDialog({ slide, index: idx, open: true });
                        }
                      }}
                    >
                      <EditRounded
                        style={{ color: theme.palette.warning.main }}
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (isFree) openFreeTrialDialog();
                        else handleSlideRemove(idx);
                      }}
                    >
                      <HighlightOffOutlined
                        style={{ color: theme.palette.warning.main }}
                      />
                    </IconButton>
                  </Box>
                )}
                <Box
                  sx={{ display: "flex", height: "100%", width: "100%" }}
                  className="c-pointer"
                  onClick={() => openFilePreview(slide, idx)}
                >
                  {showEmbedPlaceholder ? (
                    <Box className={style.socialEmbedPlaceholder}>
                      <Box className={style.socialEmbedIcon}>
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                        </svg>
                      </Box>
                      <Box className={style.socialEmbedLabel}>
                        Click to preview
                      </Box>
                    </Box>
                  ) : (
                    <FileReader
                      file={slide}
                      videoConfig={{ controls: !enablePreview }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
        {!readonly && files.length < fileLimit && (
          <Box
            key={"add-new"}
            // height="100%"
            boxSizing="border-box"
            padding=".3em"
          >
            <Box
              display="flex"
              border={"2px dashed #d5d5d5"}
              width="100%"
              height="180px"
              position="relative"
              className="slide-box"
            >
              <Button
                className={style.addNewSlide}
                onClick={
                  !isFree
                    ? () => setImportDialog({ open: true })
                    : openFreeTrialDialog
                }
              >
                <AddIcon />
                <span className={style.addNewSlideLabel}>Add new Slide</span>
              </Button>
            </Box>
          </Box>
        )}
      </Carousel>
      {importDialog && (
        <FileImportDialog
          onFileUploaded={handleFileUploaded}
          onFileRemoved={() => handleSlideRemove(importDialog.index as number)}
          open
          onClose={() => {
            setImportDialog(null);
            setReplaceInProgreess(false);
          }}
          preselectfile={importDialog.slide}
        />
      )}
      {enablePreview && previewSlide && (
        <DialogWrapper
          open
          title={`Gallery`}
          onClose={() => setPreviewSlide(null)}
          type={DialogWrapperType.DEFAULT}
          maxWidth="lg"
          height="80vh"
          enableFullScreen
          className="preview-box"
        >
          <Grid item xs={12} style={{ height: "100%" }}>
            <FileCarousel
              files={slides}
              slidesPerRow={1}
              readonly
              defaultSlideIndex={previewSlide.index}
              themeColor={themeColor}
              builderType={builderType}
            />
          </Grid>
        </DialogWrapper>
      )}
      {freeTrialDialog && (
        <FreeTrialDialog
          isOpen={freeTrialDialog}
          onClose={handleCloseFreeTrialDialog}
        />
      )}
    </>
  );
};
